/**
 * Filters for sessions.
 */
import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { SegmentedControl } from '@/components/forms/SegmentedControl';
import { Select } from '@/components/forms/Select';
import { SearchInput } from '@/components/forms/SearchInput';
import {
  SESSION_IN_PERSON_SEARCH_PLACEHOLDERS,
  SESSION_IN_PERSON_RECURRENCE_FILTER_OPTIONS,
  SESSION_IN_PERSON_ANIMATOR_FILTER_ROLE_CODES,
  SESSION_TYPE_CONFIG,
  SESSION_TYPE_FILTER_KEYS,
  DATE_RANGE_PERIOD_OPTIONS_WITH_TODAY,
  type SessionFilters as SessionFilterValues,
} from '@/components/sessions/session-configs';
import {
  getInPersonStatusConfig,
  getInPersonStatusKeysForTypes,
} from '@/components/sessions/inperson/session-table-utils';
import { SESSION_TAB_TO_TYPES } from '@/constants/shared-catalog';
import type { SessionTab } from '@/types/inperson/session.types';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { DateRangeInput } from '@/components/forms/DateRangeInput';
import { useEstablishmentsAutocomplete } from '@/hooks/useEstablishmentsAutocomplete';
import { useAdminAutocomplete } from '@/hooks/useAdminAutocomplete';
import { useSubjectsSelect } from '@/hooks/useSubjectsSelect';
import { useLevelsSelect } from '@/hooks/useLevelsSelect';

/** Props for {@link SessionFilters}. */
interface SessionFiltersProps {
  filters: SessionFilterValues;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  activeTab?: 'all' | SessionTab;
  onToggleType: (type: string) => void;
  onToggleStatus: (status: string) => void;
  onSetDateRange: (range: SessionFilterValues['dateRange']) => void;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
  onSetEstablishment: (id: string | undefined) => void;
  onSetSubject: (id: string | undefined) => void;
  onSetLevel: (id: string | undefined) => void;
  onSetSearch?: (value: string | undefined) => void;
  onSetRecurrenceType?: (type: string | undefined) => void;
  onSetAnimatorFilter?: (id: string | undefined) => void;
}

/** Filter group is allowed by RBAC and currently toggled on in the configure panel. */
function isVisible(
  filterId: string,
  allowedFilterIds: string[],
  visibleFilterIds: string[],
): boolean {
  return allowedFilterIds.includes(filterId) && visibleFilterIds.includes(filterId);
}

/** Period chips — picking a chip clears the custom date range. */
const PeriodFilter: React.FC<{
  filters: SessionFilterValues;
  onSetDateRange: (range: SessionFilterValues['dateRange']) => void;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
}> = ({ filters, onSetDateRange, onSetCustomDateRange }) => (
  <div className="filter-group">
    <span className="filter-group-label">Période</span>
    <SegmentedControl
      size="sm"
      value={filters.customDateFrom ? '' : (filters.dateRange ?? 'all')}
      onChange={(v) => {
        onSetDateRange(v as SessionFilterValues['dateRange']);
        onSetCustomDateRange(undefined, undefined);
      }}
      options={[...DATE_RANGE_PERIOD_OPTIONS_WITH_TODAY]}
    />
  </div>
);

/** Custom date range — values are local calendar dates, not UTC. */
const DateRangeFilter: React.FC<{
  filters: SessionFilterValues;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
  onSetDateRange: (range: SessionFilterValues['dateRange']) => void;
}> = ({ filters, onSetCustomDateRange, onSetDateRange }) => {
  /** Local calendar date (`YYYY-MM-DD`) — avoid UTC shift from `toISOString`. */
  const toDateString = (d?: Date) => {
    if (!d) return undefined;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fromStr = toDateString(filters.customDateFrom);
  const toStr = toDateString(filters.customDateTo);

  /** Parse as local midnight and drop the period chip (`all`). */
  const handleFromChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(undefined, filters.customDateTo);
      return;
    }
    const [year, month, day] = val.split('-').map(Number);
    const d = new Date(year, month - 1, day, 0, 0, 0);
    onSetCustomDateRange(d, filters.customDateTo);
    onSetDateRange('all');
  };

  /** Parse as local end-of-day and drop the period chip (`all`). */
  const handleToChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(filters.customDateFrom, undefined);
      return;
    }
    const [year, month, day] = val.split('-').map(Number);
    const d = new Date(year, month - 1, day, 23, 59, 59);
    onSetCustomDateRange(filters.customDateFrom, d);
    onSetDateRange('all');
  };

  return (
    <div className="filter-group">
      <span className="filter-group-label">Date personnalisée</span>
      <div className="w-auto mt-0.5">
        <DateRangeInput
          startDate={fromStr}
          endDate={toStr}
          onStartDateChange={handleFromChange}
          onEndDateChange={handleToChange}
          layout="horizontal"
          showConnector={true}
          showPresets={false}
          hidePickerLabels={true}
          compact={true}
        />
      </div>
    </div>
  );
};

/** Type chips — hidden on the information-meeting tab (single type). */
const TypeFilter: React.FC<{
  filters: SessionFilterValues;
  onToggleType: (type: string) => void;
  activeTab?: 'all' | SessionTab;
}> = ({ filters, onToggleType, activeTab }) => {
  const typesForTab = React.useMemo((): readonly string[] => {
    if (activeTab === 'tutoring') {
      return SESSION_TAB_TO_TYPES['tutoring'];
    }
    if (activeTab === 'information-meeting') {
      return [];
    }
    return SESSION_TYPE_FILTER_KEYS;
  }, [activeTab]);

  if (typesForTab.length === 0) return null;

  return (
    <div className="filter-group">
      <span className="filter-group-label">
        {!activeTab || activeTab === 'all' ? 'Type de séance' : 'Type'}
      </span>
      <div className="filter-options">
        {typesForTab.map((key) => {
          const config = SESSION_TYPE_CONFIG[key as keyof typeof SESSION_TYPE_CONFIG];
          if (!config) return null;
          const Icon = config.icon;
          const isActive = filters.types.includes(key);
          return (
            <Button
              key={key}
              variant="custom"
              size="sm"
              icon={Icon}
              iconPosition="left"
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                isActive ? 'filter-badge-active' : 'filter-badge-inactive',
              )}
              style={{ '--filter-color': config.badgeColor } as React.CSSProperties}
              onClick={() => onToggleType(key)}
            >
              {config.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

/** Session-status chips scoped to the active in-person tab. */
const StatusFilter: React.FC<{
  filters: SessionFilterValues;
  onToggleStatus: (status: string) => void;
  activeTab?: 'all' | SessionTab;
}> = ({ filters, onToggleStatus, activeTab }) => {
  let typesForStatuses: string[] = [];
  if (activeTab === 'information-meeting') {
    typesForStatuses = ['information_meeting_in_person'];
  } else if (activeTab === 'tutoring') {
    typesForStatuses = [...SESSION_TAB_TO_TYPES.tutoring];
  }
  const statusKeys = getInPersonStatusKeysForTypes(typesForStatuses);
  const labelType =
    activeTab === 'information-meeting' ? 'information_meeting_in_person' : undefined;

  return (
    <div className="filter-group">
      <span className="filter-group-label">Statut</span>
      <div className="filter-options">
        {statusKeys.map((key) => {
          const config = getInPersonStatusConfig(key, labelType);
          const Icon = config.icon;
          const isActive = filters.statuses.includes(key);

          const getBadgeClassName = () => {
            if (!isActive) return 'filter-badge-inactive';
            if (config.filterActiveVariant === 'solid') return 'filter-badge-active-solid';
            return 'filter-badge-active';
          };

          return (
            <Button
              key={key}
              variant="custom"
              size="sm"
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                getBadgeClassName(),
              )}
              style={{ '--filter-color': config.badgeColor } as React.CSSProperties}
              onClick={() => onToggleStatus(key)}
            >
              {config.isInProgress ? (
                <span
                  className="w-3 h-3 rounded-full shrink-0"
                  style={{ background: isActive ? 'white' : 'rgb(var(--color-error))' }}
                />
              ) : (
                <Icon className="w-3.5 h-3.5" />
              )}
              {config.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

/** Establishment autocomplete for in-person session filters. */
const EstablishmentFilter: React.FC<{
  filters: SessionFilterValues;
  onSetEstablishment: (id: string | undefined) => void;
}> = ({ filters, onSetEstablishment }) => {
  const { options, isLoading, onSearch, onLoadMore, hasMore } = useEstablishmentsAutocomplete();

  return (
    <div className="filter-group">
      <span className="filter-group-label">Établissement</span>
      <div className="w-64">
        <Autocomplete
          placeholder="Rechercher un établissement..."
          options={options}
          value={filters.establishmentId ?? ''}
          onChange={(val) => onSetEstablishment(val || undefined)}
          onSearch={onSearch}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoading={isLoading}
          minSearchLength={2}
          inputSize="sm"
        />
      </div>
    </div>
  );
};

/** Subject select for in-person session filters. */
const SubjectFilter: React.FC<{
  filters: SessionFilterValues;
  onSetSubject: (id: string | undefined) => void;
  enabled: boolean;
}> = ({ filters, onSetSubject, enabled }) => {
  const { options, isLoading, onLoadMore, hasMore } = useSubjectsSelect({ enabled });

  const selectOptions = [{ value: '', label: 'Toutes' }, ...options];

  return (
    <div className="filter-group">
      <span className="filter-group-label">Matière</span>
      <Select
        options={selectOptions}
        value={filters.subjectId ?? ''}
        onChange={(val) => onSetSubject(val || undefined)}
        onLoadMore={onLoadMore}
        hasMore={hasMore}
        isLoading={isLoading}
        size="sm"
        className="min-w-36"
      />
    </div>
  );
};

/** Level select for in-person session filters. */
const LevelFilter: React.FC<{
  filters: SessionFilterValues;
  onSetLevel: (id: string | undefined) => void;
  enabled: boolean;
}> = ({ filters, onSetLevel, enabled }) => {
  const { options, isLoading, onLoadMore, hasMore } = useLevelsSelect({ enabled });

  const selectOptions = [{ value: '', label: 'Tous' }, ...options];

  return (
    <div className="filter-group">
      <span className="filter-group-label">Niveau</span>
      <Select
        options={selectOptions}
        value={filters.levelId ?? ''}
        onChange={(val) => onSetLevel(val || undefined)}
        onLoadMore={onLoadMore}
        hasMore={hasMore}
        isLoading={isLoading}
        size="sm"
        className="min-w-36"
      />
    </div>
  );
};

/** Free-text search for in-person session filters. */
const SearchFilter: React.FC<{
  filters: SessionFilterValues;
  onSetSearch: (value: string | undefined) => void;
  activeTab?: 'all' | SessionTab;
}> = ({ filters, onSetSearch, activeTab }) => (
  <div className="filter-group">
    <span className="filter-group-label">Recherche</span>
    <div className="w-56">
      <SearchInput
        size="sm"
        shortcut={false}
        debounceMs={300}
        placeholder={SESSION_IN_PERSON_SEARCH_PLACEHOLDERS[activeTab ?? 'all']}
        value={filters.search ?? ''}
        onSearch={(q) => onSetSearch(q.trim() || undefined)}
        onClear={() => onSetSearch(undefined)}
      />
    </div>
  </div>
);

/** Recurrence-type select for in-person session filters. */
const RecurrenceFilter: React.FC<{
  filters: SessionFilterValues;
  onSetRecurrenceType: (type: string | undefined) => void;
}> = ({ filters, onSetRecurrenceType }) => {
  const selectOptions = SESSION_IN_PERSON_RECURRENCE_FILTER_OPTIONS;

  return (
    <div className="filter-group">
      <span className="filter-group-label">Récurrence</span>
      <Select
        options={selectOptions}
        value={filters.recurrenceType ?? ''}
        onChange={(val) => onSetRecurrenceType(val || undefined)}
        size="sm"
        className="min-w-36"
      />
    </div>
  );
};

/** Animator autocomplete for in-person session filters. */
const AnimatorFilter: React.FC<{
  filters: SessionFilterValues;
  onSetAnimatorFilter: (id: string | undefined) => void;
}> = ({ filters, onSetAnimatorFilter }) => {
  const { options, isLoading, onSearch, onLoadMore, hasMore } = useAdminAutocomplete({
    roleCodes: [...SESSION_IN_PERSON_ANIMATOR_FILTER_ROLE_CODES],
  });

  return (
    <div className="filter-group">
      <span className="filter-group-label">Animateur</span>
      <div className="w-64">
        <Autocomplete
          placeholder="Rechercher un animateur..."
          options={options.map((opt) => ({ value: opt.value, label: opt.label }))}
          value={filters.animatorUserId ?? ''}
          onChange={(val) => onSetAnimatorFilter(val || undefined)}
          onSearch={onSearch}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoading={isLoading}
          minSearchLength={2}
          inputSize="sm"
        />
      </div>
    </div>
  );
};

/** Filters for sessions. */
export const SessionFilters: React.FC<SessionFiltersProps> = ({
  filters,
  allowedFilterIds,
  visibleFilterIds,
  activeTab,
  onToggleType,
  onToggleStatus,
  onSetDateRange,
  onSetCustomDateRange,
  onSetEstablishment,
  onSetSubject,
  onSetLevel,
  onSetSearch,
  onSetRecurrenceType,
  onSetAnimatorFilter,
}) => {
  const show = (id: string) => isVisible(id, allowedFilterIds, visibleFilterIds);
  const isInformationTab = activeTab === 'information-meeting';

  return (
    <div className="filter-groups-container">
      {show('filter-search') && onSetSearch && (
        <SearchFilter filters={filters} onSetSearch={onSetSearch} activeTab={activeTab} />
      )}

      {show('filter-period') && (
        <PeriodFilter
          filters={filters}
          onSetDateRange={onSetDateRange}
          onSetCustomDateRange={onSetCustomDateRange}
        />
      )}

      {show('filter-date-range') && (
        <DateRangeFilter
          filters={filters}
          onSetCustomDateRange={onSetCustomDateRange}
          onSetDateRange={onSetDateRange}
        />
      )}

      {show('filter-type') && (
        <TypeFilter filters={filters} onToggleType={onToggleType} activeTab={activeTab} />
      )}

      {show('filter-status') && (
        <StatusFilter filters={filters} onToggleStatus={onToggleStatus} activeTab={activeTab} />
      )}

      {show('filter-establishment') && (
        <EstablishmentFilter filters={filters} onSetEstablishment={onSetEstablishment} />
      )}

      {!isInformationTab && show('filter-subject') && (
        <SubjectFilter filters={filters} onSetSubject={onSetSubject} enabled={true} />
      )}

      {!isInformationTab && show('filter-level') && (
        <LevelFilter filters={filters} onSetLevel={onSetLevel} enabled={true} />
      )}

      {!isInformationTab && show('filter-recurrence') && onSetRecurrenceType && (
        <RecurrenceFilter filters={filters} onSetRecurrenceType={onSetRecurrenceType} />
      )}

      {isInformationTab && show('filter-animator') && onSetAnimatorFilter && (
        <AnimatorFilter filters={filters} onSetAnimatorFilter={onSetAnimatorFilter} />
      )}
    </div>
  );
};
